import { useMemo, useState } from 'react'
import {
  BatteryCharging, Boxes, Cctv, Cog, CreditCard, Cpu, Disc3, DoorOpen, Flame, Lightbulb, Monitor, Network,
  Printer, Server, ShoppingCart, Volume2, Wifi, Zap,
} from 'lucide-react'
import { Shell, useShellMeta } from '../../../admin/Shell.js'
import { usePermissions } from '../../../admin/primitives.js'
import { SegmentedControl } from '../../../admin/pickers.js'
import { Link } from '../../../router.js'
import {
  BandeauFige, BandeauLecture, LIBELLE_NIVEAU, Pastille, TON_ALIM, fmtAge, fmtRatio, nomSalle, renommerSalles, useVueSonde, type Ton,
} from '../commun.js'
import { estFige, tonNiveau, tonSalle } from '../hub/modele.js'
import {
  BarreEtats, Fait, IncidentsBrefs, LigneAppareil, Quand, Retour, Section, Squelette, Vide, donneeDe,
} from './composants.js'
import {
  CHEMIN_A_VALIDER, CODE_SALLE, LIBELLE_ORIGINE_FENETRE, LIBELLE_SOURCE_ALIM, aRegarder, cheminAppareil, estDocSalle,
  type CarteSalle, type DocSalle, type Fenetre,
} from './modele.js'
import { useMenuSalle } from '../menu-salle.js'
import { BoutonActualiser } from '../actualiser.js'

/**
 * FICHE SALLE — /infrastructure/salles/:code (21/10/2026).
 *
 * Lit `zones/:code` de Sonde. En tête : le niveau de la salle et son
 * alimentation, en couleurs ; « éteinte comme prévu » est NEUTRE, jamais une
 * panne. Puis la fenêtre d'exploitation, le score et son explication, les
 * appareils par famille (prêt pour des centaines : repliés, filtrables) et
 * les incidents de la salle.
 */

const ICONE_FAMILLE: Record<string, typeof Network> = {
  network: Network, wifi: Wifi, video: Cctv, access: DoorOpen, lighting: Lightbulb, sound: Volume2, dj: Disc3,
  pos: ShoppingCart, payment: CreditCard, iot: Cpu, fire_safety: Flame, server: Server, service: Cog,
  power: BatteryCharging, display: Monitor, printing: Printer,
}

const REPLI = 8

function phraseSalle(z: CarteSalle): string {
  switch (z.power.situation) {
    case 'mains_outage': return 'Coupure de courant : la salle est sans secteur. L’alerte porte sur la coupure, pas sur chaque appareil.'
    case 'off_unexpected': return 'Salle coupée pendant l’exploitation : son alimentation technique est tombée.'
    case 'off_as_planned': return 'Éteinte comme prévu, hors exploitation : ce n’est pas une panne. Ses appareils éteints ne comptent pas dans le score.'
  }
  if (z.power.situation === 'on') return phraseNiveau(z, true)
  return `Alimentation inconnue : aucun témoin de courant n’est lu, rien n’est déclaré en panne sur cette base. ${phraseNiveau(z, false)}`
}

/** Le niveau de la salle en clair ; `allumee` = le courant est constaté. */
function phraseNiveau(z: CarteSalle, allumee: boolean): string {
  const c = z.counts
  const aveugles = c.unknown + c.stale + c.unavailable
  if (c.total === 0) return 'Aucun appareil de la salle n’est encore suivi.'
  if (z.level === 'DEGRADED') return `Dégradée : ${c.offline} appareil${c.offline > 1 ? 's' : ''} hors ligne${c.degraded ? `, ${c.degraded} dégradé${c.degraded > 1 ? 's' : ''}` : ''}.`
  if (z.level === 'ATTENTION') {
    const n = c.degraded + c.offline
    return n > 0 ? `${allumee ? 'Allumée, ' : ''}${n} appareil${n > 1 ? 's' : ''} à surveiller.` : 'À surveiller : voir les incidents de la salle.'
  }
  if (z.level === 'OPERATIONAL') {
    const base = allumee ? 'Allumée et opérationnelle' : 'Opérationnelle'
    return aveugles > 0
      ? `${base}. ${aveugles} appareil${aveugles > 1 ? 's' : ''} sans donnée récente, comptés à part — ni en panne, ni en ligne.`
      : `${base} : tout ce que Sonde suit répond normalement.`
  }
  return 'Niveau non calculé pour l’instant.'
}

function texteFenetre(f: Fenetre): string {
  const origine = f.label ?? LIBELLE_ORIGINE_FENETRE[f.origin] ?? f.origin
  return `${origine}`
}

function Fenetres({ z }: { z: CarteSalle }) {
  if (z.window) {
    return (
      <>
        <strong>En cours</strong> · <Quand iso={z.window.starts_at} /> → <Quand iso={z.window.ends_at} />
        <span className="sv-fiche-fait__sous">{texteFenetre(z.window)}{z.operating ? ' · exploitation' : ''}</span>
      </>
    )
  }
  if (z.next_window) {
    return (
      <>
        Prochaine · <Quand iso={z.next_window.starts_at} /> → <Quand iso={z.next_window.ends_at} />
        <span className="sv-fiche-fait__sous">{texteFenetre(z.next_window)}</span>
      </>
    )
  }
  return <>Aucune fenêtre prévue</>
}

function EnTete({ d, fige }: { d: DocSalle; fige: boolean }) {
  const z = d.zone
  const vide = z.counts.total === 0
  // UNE règle pour le hub et la fiche (hub/modele.ts) : niveau ET alimentation ;
  // figé ou périmé = gris ; éteinte comme prévu = neutre, jamais rouge.
  const tonFiche: Ton = tonSalle(z, fige)
  const tonNiv: Ton = tonNiveau(z.level, fige)
  const tonAlim: Ton = fige ? 'gris' : TON_ALIM[z.power.situation] ?? 'gris'
  const menuSalle = useMenuSalle()
  return (
    <header className={`sv-fiche-hero sv-fiche-hero--${tonFiche}`} {...menuSalle.salle({ code: z.code, label: z.label })}>
      <div className="sv-fiche-hero__haut">
        <div className="sv-fiche-hero__id">
          <p className="sv-fiche-hero__sur">Salle</p>
          <h1 className="lc-title sv-fiche-hero__titre">{nomSalle(z.code, z.label)}</h1>
          <div className="sv-fiche-hero__pastilles">
            {!vide ? <Pastille ton={tonNiv}>{z.level ? LIBELLE_NIVEAU[z.level] ?? z.level : 'Niveau inconnu'}</Pastille> : null}
            <Pastille ton={tonAlim}>{z.power.situation === 'unknown' ? 'Alimentation inconnue' : z.power.label_fr}</Pastille>
            {z.operating ? <Pastille ton={fige ? 'gris' : 'ok'}>En exploitation</Pastille> : null}
          </div>
        </div>
        <div className="sv-fiche-hero__gestes">
          <BoutonActualiser cible={{ kind: 'zone', code: z.code.toUpperCase(), nom: nomSalle(z.code, z.label) }} libelle="Actualiser la salle" />
        </div>
        <div className={`sv-fiche-score sv-fiche-score--${vide ? 'gris' : tonNiv}`} title={renommerSalles(z.score_detail.explanation_fr)}>
          <span className="sv-fiche-score__n">{z.score ?? '—'}</span>
          <span className="sv-fiche-score__l">{z.score === null ? 'score non calculé' : 'score sur 100'}</span>
        </div>
      </div>
      <p className="sv-conseq sv-fiche-hero__phrase">{phraseSalle(z)}</p>
      <BarreEtats comptes={z.counts} />
      <dl className="sv-fiche-faits">
        <Fait libelle="Alimentation" sous={<>{z.power.since ? <>depuis <Quand iso={z.power.since} /> · </> : null}{LIBELLE_SOURCE_ALIM[z.power.source] ?? z.power.source}</>}>
          {z.power.label_fr}
        </Fait>
        <Fait libelle="Exploitation"><Fenetres z={z} /></Fait>
        <Fait libelle="Incidents ouverts" sous={z.incidents_open ? 'voir plus bas' : null}>{z.incidents_open}</Fait>
        <Fait libelle="Appareils" sous={<>données {fmtAge(z.data_age_s)} · Sonde</>}>{z.counts.total ? `${fmtRatio(z.counts.online, z.counts.total)} en ligne` : 'Aucun au registre'}</Fait>
      </dl>
    </header>
  )
}

function Alimentation({ d, peutReglages }: { d: DocSalle; peutReglages: boolean }) {
  const p = d.power_domain
  return (
    <Section titre="Alimentation" id="sv-sal-alim" indice={LIBELLE_SOURCE_ALIM[d.zone.power.source]}>
      <p className="sv-fiche-texte">
        <Pastille ton={TON_ALIM[d.zone.power.situation] ?? 'gris'}>{d.zone.power.label_fr}</Pastille>
        {d.zone.power.since ? <> depuis <Quand iso={d.zone.power.since} /></> : null}
      </p>
      {p ? (
        <dl className="sv-fiche-dl">
          <div><dt>Domaine</dt><dd>{renommerSalles(p.label)} <span className="sv-mono">{p.code}</span></dd></div>
          <div>
            <dt>Comment Sonde le juge</dt>
            <dd>
              {p.mode === 'root'
                ? p.root_uid ? <>Témoin : <Link href={cheminAppareil(p.root_uid)}>{p.root_uid}</Link> — la salle est allumée quand il répond.</> : 'Témoin : non désigné.'
                : 'Vote des appareils de la salle : aucun témoin unique.'}
            </dd>
          </div>
          <div><dt>Origine</dt><dd>{p.learned ? `Appris par Sonde (${p.learned_cycles} cycle${p.learned_cycles > 1 ? 's' : ''} observé${p.learned_cycles > 1 ? 's' : ''})` : 'Déclaré'}</dd></div>
          <div><dt>Statut du domaine</dt><dd>{p.status}</dd></div>
        </dl>
      ) : (
        <p className="sv-fiche-texte sv-fiche-doux">
          Aucun domaine d’alimentation n’est rattaché à cette salle dans Sonde.
          {peutReglages ? <> Le témoin et le vote se règlent dans <Link href="/settings/supervision">Paramètres › Supervision</Link>.</> : null}
        </p>
      )}
    </Section>
  )
}

function Score({ z }: { z: CarteSalle }) {
  const s = z.score_detail
  return (
    <Section titre="Score de la salle" id="sv-sal-score" indice={z.score === null ? '—' : `${z.score}/100`}>
      <p className="sv-fiche-texte">{renommerSalles(s.explanation_fr)}</p>
      <ul className="sv-fiche-chiffres">
        <li><strong>{s.evaluated}</strong> évalué{s.evaluated > 1 ? 's' : ''}</li>
        <li><strong>{s.excluded_off_expected}</strong> éteint{s.excluded_off_expected > 1 ? 's' : ''} comme prévu, hors score</li>
        <li><strong>{s.excluded_maintenance}</strong> en maintenance, hors score</li>
        <li><strong>{s.excluded_unobserved}</strong> non observé{s.excluded_unobserved > 1 ? 's' : ''}, hors score</li>
        <li><strong>{s.consequences}</strong> touché{s.consequences > 1 ? 's' : ''} par un amont, compté{s.consequences > 1 ? 's' : ''} une fois</li>
      </ul>
    </Section>
  )
}

function Familles({ d, peutRegistre }: { d: DocSalle; peutRegistre: boolean }) {
  const [filtre, setFiltre] = useState<'tous' | 'regarder'>('tous')
  const [ouvertes, setOuvertes] = useState<Record<string, boolean>>({})
  const nRegarder = useMemo(() => d.families.reduce((n, f) => n + f.devices.filter((a) => aRegarder(a.state.display_state)).length, 0), [d])
  const familles = d.families
    .map((f) => ({ ...f, devices: filtre === 'tous' ? f.devices : f.devices.filter((a) => aRegarder(a.state.display_state)) }))
    .filter((f) => f.devices.length > 0)

  return (
    <Section titre="Appareils par famille" id="sv-sal-familles" indice={`${d.zone.counts.total} au registre`}
             actions={d.zone.counts.total > 0 ? (
               <SegmentedControl size="sm" ariaLabel="Filtrer les appareils" value={filtre} onChange={setFiltre}
                 options={[{ value: 'tous', label: 'Tous' }, { value: 'regarder', label: `À regarder (${nRegarder})` }]} />
             ) : undefined}>
      {d.families.length === 0 ? (
        <Vide>
          <strong>Aucun appareil de cette salle n’est encore au registre de Sonde.</strong>
          <span>Les équipements que Sonde a découverts attendent d’être validés avant d’être suivis ici.</span>
          {peutRegistre ? <Link className="lc-btn" href={CHEMIN_A_VALIDER}>Ouvrir « À valider »</Link> : <span>Une personne qui gère le registre peut les valider dans « À valider ».</span>}
        </Vide>
      ) : familles.length === 0 ? (
        <Vide><span>Rien à regarder : tous les appareils de la salle sont en ligne, éteints comme prévu ou en maintenance.</span></Vide>
      ) : (
        <div className="sv-fiche-familles">
          {familles.map((f) => {
            const Icone = ICONE_FAMILLE[f.family] ?? Boxes
            const ouverte = ouvertes[f.family] ?? false
            const visibles = ouverte ? f.devices : f.devices.slice(0, REPLI)
            return (
              <article key={f.family} className="sv-fiche-famille">
                <header className="sv-fiche-famille__tete">
                  <span className="sv-fiche-famille__icone" aria-hidden="true"><Icone size={18} /></span>
                  <h3 className="sv-fiche-famille__titre">{f.label}</h3>
                  <span className="sv-fiche-famille__ratio">{fmtRatio(f.counts.online, f.counts.total)} en ligne</span>
                </header>
                <BarreEtats comptes={f.counts} legende={false} />
                <ul className="sv-fiche-liste">
                  {visibles.map((a) => <LigneAppareil key={a.uid} a={a} />)}
                </ul>
                {f.devices.length > REPLI ? (
                  <button type="button" className="lc-btn lc-btn--ghost sv-fiche-plus" onClick={() => setOuvertes((o) => ({ ...o, [f.family]: !ouverte }))}>
                    {ouverte ? 'Replier' : `Afficher les ${f.devices.length - REPLI} autres`}
                  </button>
                ) : null}
              </article>
            )
          })}
        </div>
      )}
    </Section>
  )
}

export function FicheSallePage({ code }: { code: string }) {
  const meta = useShellMeta()
  const { permissions } = usePermissions()
  const c = code.toUpperCase()
  const valide = CODE_SALLE.test(c)
  const { lecture, precedente } = useVueSonde(valide ? `zones/${encodeURIComponent(c)}` : null, estDocSalle, 15_000)
  const { data: d, perime } = donneeDe(lecture, precedente)
  // Collecteur muet ou lecture en échec : la dernière donnée reste, TOUT en gris.
  const fige = perime || estFige(d?.collector)

  return (
    <Shell active="infrastructure" meta={meta}>
      <div className="lc-page lc-page--wide lc-stagger sv-fiche">
        <Retour repli="/infrastructure" libelle="Supervision" />
        {!valide ? <Vide><strong>Adresse de salle invalide.</strong><span>Revenez à la vue d’ensemble et choisissez la salle dans la liste.</span></Vide> : null}
        <BandeauLecture lecture={lecture} depuis={precedente?.generated_at ?? null} />
        {d ? <BandeauFige collector={d.collector} /> : null}
        {valide && !d && !lecture ? <Squelette lignes={6} /> : null}
        {d ? (
          <div className={fige ? 'sv-fiche-perime' : undefined}>
            <EnTete d={d} fige={fige} />
            <div className="sv-fiche-grille">
              <div className="sv-fiche-grille__cote">
                <Section titre="Incidents de la salle" id="sv-sal-inc" indice={d.incidents.length ? `${d.incidents.length} en cours` : undefined}
                         actions={<Link className="lc-hint" href="/infrastructure/incidents">Tous les incidents</Link>}>
                  <IncidentsBrefs items={d.incidents} vide="Aucun incident en cours dans cette salle." />
                </Section>
                <Alimentation d={d} peutReglages={permissions.includes('sonde.settings.manage')} />
                <Score z={d.zone} />
              </div>
              <div className="sv-fiche-grille__principal">
                <Familles d={d} peutRegistre={permissions.includes('sonde.registry.manage') || permissions.includes('sonde.details.read')} />
              </div>
            </div>
          </div>
        ) : null}
        <p className="sv-fiche-pied"><Zap size={14} aria-hidden="true" /> Source : Sonde{d ? <> · lu à <Quand iso={d.generated_at} /></> : null}</p>
      </div>
    </Shell>
  )
}
